import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { goalsApi, milestonesApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, 
  Plus, 
  Target, 
  AlertTriangle, 
  CheckCircle2, 
  Calendar,
  TrendingDown,
  Pencil,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatCurrency, getStatusColor, getHorizonColor, getDepartmentColor } from '../lib/utils';
import { computeAllowedEndDate, isFixedDurationCycle, validateDateRange } from '../lib/dateUtils';

const MILESTONE_TYPES = ["Weekly", "15-Day", "30-Day", "35-Day", "45-Day", "Monthly", "6-Month"];

export default function GoalDetail() {
  const { goalId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [goal, setGoal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [milestoneDialogOpen, setMilestoneDialogOpen] = useState(false);
  const [editingMilestone, setEditingMilestone] = useState(null);

  useEffect(() => {
    loadGoalDetail();
  }, [goalId]);

  const loadGoalDetail = async () => {
    try {
      const response = await goalsApi.getDetail(goalId);
      setGoal(response.data);
    } catch (error) {
      toast.error('Failed to load goal details');
      navigate('/goals');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;
    try {
      await milestonesApi.delete(milestoneId);
      toast.success('Milestone deleted');
      loadGoalDetail();
    } catch (error) {
      toast.error('Failed to delete milestone');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton" />
        <div className="h-64 skeleton rounded-lg" />
      </div>
    );
  }

  if (!goal) {
    return <div>Goal not found</div>;
  }

  const { deficit, milestones, owner, outcomes_count, total_actual } = goal;
  const isNumericGoal = ['Currency', 'Count', 'Percent'].includes(goal.metric_type);

  return (
    <div className="space-y-6" data-testid="goal-detail-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate('/goals')} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Goals
      </Button>

      {/* Goal Header */}
      <Card className="border border-slate-200">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={getHorizonColor(goal.horizon)}>{goal.horizon}</Badge>
                <Badge variant="outline" className={getDepartmentColor(goal.department)}>
                  {goal.department}
                </Badge>
                <Badge variant="outline" className={getStatusColor(goal.status)}>
                  {goal.status}
                </Badge>
              </div>
              <h1 className="text-2xl font-bold mb-2">{goal.title}</h1>
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <span>Owner: {owner?.name || 'Not assigned'}</span>
                <span>Period: {formatDate(goal.start_date)} - {formatDate(goal.end_date)}</span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">
                {goal.metric_type === 'Yes-No' ? (
                  goal.status === 'Completed' ? 'Done' : 'Pending'
                ) : goal.target_value > 0 ? (
                  formatCurrency(goal.target_value, goal.unit)
                ) : (
                  'N/A'
                )}
              </div>
              <p className="text-sm text-slate-500">Target</p>
              {isNumericGoal && goal.target_value > 0 && (
                <div className="mt-2">
                  <Progress value={goal.progress || 0} className="w-32 h-2" />
                  <p className="text-sm mt-1">{Math.round(goal.progress || 0)}% complete</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Deficit Warning */}
      {isNumericGoal && deficit?.is_behind && (
        <Alert className="bg-red-50 border-red-200">
          <TrendingDown className="h-5 w-5 text-red-600" />
          <AlertTitle className="text-red-800 font-semibold">Behind Target</AlertTitle>
          <AlertDescription className="text-red-700">
            You are behind by {formatCurrency(deficit.deficit, goal.unit)} as of today.
            <br />
            <span className="text-sm">
              Expected: {formatCurrency(deficit.expected_to_date, goal.unit)} | 
              Achieved: {formatCurrency(deficit.achieved_to_date, goal.unit)}
            </span>
          </AlertDescription>
        </Alert>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{milestones?.length || 0}</p>
                <p className="text-xs text-slate-500">Milestones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{milestones?.filter(m => m.status === 'Closed').length || 0}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <Calendar className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{outcomes_count || 0}</p>
                <p className="text-xs text-slate-500">Linked Outcomes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {isNumericGoal ? formatCurrency(total_actual || 0, goal.unit) : '-'}
                </p>
                <p className="text-xs text-slate-500">Total Actual</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Milestones Section */}
      <Card className="border border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold">Milestones</CardTitle>
          {isAdmin() && (
            <Button 
              onClick={() => { setEditingMilestone(null); setMilestoneDialogOpen(true); }}
              data-testid="create-milestone-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Create Milestone
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {milestones?.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Actual</TableHead>
                  <TableHead>Progress</TableHead>
                  <TableHead>Deficit</TableHead>
                  <TableHead>Status</TableHead>
                  {isAdmin() && <TableHead className="w-24">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {milestones.map(milestone => {
                  const milestoneDeficit = isNumericGoal && milestone.target_value > 0 
                    ? Math.max(milestone.target_value - milestone.actual_value, 0) 
                    : 0;
                  const hasMilestoneDeficit = milestoneDeficit > 0 && milestone.status === 'Open';
                  
                  return (
                    <TableRow key={milestone.id} className={hasMilestoneDeficit ? 'bg-red-50' : ''}>
                      <TableCell>
                        <Badge className={getHorizonColor(milestone.milestone_type)}>
                          {milestone.milestone_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(milestone.start_date)} - {formatDate(milestone.end_date)}
                      </TableCell>
                      <TableCell>
                        {isNumericGoal ? formatCurrency(milestone.target_value, goal.unit) : '-'}
                      </TableCell>
                      <TableCell>
                        {isNumericGoal ? formatCurrency(milestone.actual_value, goal.unit) : '-'}
                      </TableCell>
                      <TableCell>
                        {isNumericGoal && milestone.target_value > 0 ? (
                          <div className="flex items-center gap-2">
                            <Progress value={milestone.progress || 0} className="w-16 h-2" />
                            <span className="text-sm">{Math.round(milestone.progress || 0)}%</span>
                          </div>
                        ) : (
                          <span className="text-slate-400">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {hasMilestoneDeficit ? (
                          <span className="text-red-600 font-medium">
                            -{formatCurrency(milestoneDeficit, goal.unit)}
                          </span>
                        ) : (
                          <span className="text-emerald-600">On track</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={milestone.status === 'Closed' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}>
                          {milestone.status}
                        </Badge>
                      </TableCell>
                      {isAdmin() && (
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => { setEditingMilestone(milestone); setMilestoneDialogOpen(true); }}
                              data-testid={`edit-milestone-${milestone.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteMilestone(milestone.id)}
                              data-testid={`delete-milestone-${milestone.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-slate-500">
              <Target className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p>No milestones created yet</p>
              {isAdmin() && (
                <p className="text-sm mt-2">Create milestones to track intermediate targets</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Milestone Dialog */}
      <Dialog open={milestoneDialogOpen} onOpenChange={setMilestoneDialogOpen}>
        <MilestoneDialog
          key={editingMilestone?.id || 'new'}
          milestone={editingMilestone}
          parentGoal={goal}
          onClose={() => { setMilestoneDialogOpen(false); setEditingMilestone(null); }}
          onSave={loadGoalDetail}
        />
      </Dialog>
    </div>
  );
}

function MilestoneDialog({ milestone, parentGoal, onClose, onSave }) {
  const isNumericGoal = ['Currency', 'Count', 'Percent'].includes(parentGoal.metric_type);
  
  const getInitialForm = (m) => ({
    parent_goal_id: parentGoal.id,
    milestone_type: m?.milestone_type || 'Weekly',
    start_date: m?.start_date || '',
    end_date: m?.end_date || '',
    target_value: m?.target_value || 0,
    status: m?.status || 'Open',
    notes: m?.notes || '',
  });

  const [form, setForm] = useState(getInitialForm(milestone));
  const [loading, setLoading] = useState(false);
  const [maxEndDate, setMaxEndDate] = useState('');

  useEffect(() => {
    setForm(getInitialForm(milestone));
  }, [milestone]);

  // Update max end date when start_date or milestone_type changes
  useEffect(() => {
    if (form.start_date && form.milestone_type) {
      const allowed = computeAllowedEndDate(form.start_date, form.milestone_type);
      setMaxEndDate(allowed);
      
      // For fixed duration cycles, auto-fill end date
      if (isFixedDurationCycle(form.milestone_type)) {
        setForm(prev => ({ ...prev, end_date: allowed }));
      } else {
        // For variable cycles, clear end date if it's beyond allowed
        if (form.end_date && form.end_date > allowed) {
          setForm(prev => ({ ...prev, end_date: '' }));
        }
      }
    }
  }, [form.start_date, form.milestone_type]);

  const handleMilestoneTypeChange = (newType) => {
    // When type changes, recalculate end date if start date exists
    const newForm = { ...form, milestone_type: newType };
    if (form.start_date) {
      const allowed = computeAllowedEndDate(form.start_date, newType);
      if (isFixedDurationCycle(newType)) {
        newForm.end_date = allowed;
      } else {
        // Keep current end date only if it's within allowed range
        if (form.end_date && form.end_date > allowed) {
          newForm.end_date = '';
        }
      }
    }
    setForm(newForm);
  };

  const handleStartDateChange = (newStartDate) => {
    const newForm = { ...form, start_date: newStartDate };
    if (newStartDate && form.milestone_type) {
      const allowed = computeAllowedEndDate(newStartDate, form.milestone_type);
      if (isFixedDurationCycle(form.milestone_type)) {
        newForm.end_date = allowed;
      } else {
        // Clear end date if it's now invalid
        if (form.end_date && form.end_date > allowed) {
          newForm.end_date = '';
        }
      }
    }
    setForm(newForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!form.start_date || !form.end_date) {
      toast.error('Start and end dates are required');
      return;
    }
    
    // Validate date range using shared function
    const validation = validateDateRange(form.start_date, form.end_date, form.milestone_type);
    if (!validation.isValid) {
      toast.error(validation.error);
      return;
    }
    
    if (isNumericGoal && (!form.target_value || form.target_value <= 0)) {
      toast.error('Target value must be greater than 0');
      return;
    }
    
    setLoading(true);
    try {
      if (milestone?.id) {
        await milestonesApi.update(milestone.id, form);
        toast.success('Milestone updated');
      } else {
        await milestonesApi.create(form);
        toast.success('Milestone created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save milestone');
    } finally {
      setLoading(false);
    }
  };

  const isFixedCycle = isFixedDurationCycle(form.milestone_type);

  return (
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>{milestone ? 'Edit Milestone' : 'Create Milestone'}</DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label>Milestone Type</Label>
          <Select value={form.milestone_type} onValueChange={handleMilestoneTypeChange}>
            <SelectTrigger data-testid="milestone-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MILESTONE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input
              type="date"
              value={form.start_date}
              onChange={(e) => handleStartDateChange(e.target.value)}
              required
              data-testid="milestone-start-date"
            />
          </div>
          <div className="space-y-2">
            <Label>End Date {isFixedCycle && <span className="text-xs text-slate-500">(auto)</span>}</Label>
            <Input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({...form, end_date: e.target.value})}
              min={form.start_date}
              max={maxEndDate}
              required
              disabled={isFixedCycle}
              className={isFixedCycle ? 'bg-slate-100' : ''}
              data-testid="milestone-end-date"
            />
            {maxEndDate && (
              <p className="text-xs text-slate-500">
                Max allowed: {maxEndDate}
              </p>
            )}
          </div>
        </div>

        {isNumericGoal && (
          <div className="space-y-2">
            <Label>Target Value ({parentGoal.unit})</Label>
            <Input
              type="number"
              value={form.target_value}
              onChange={(e) => setForm({...form, target_value: parseFloat(e.target.value) || 0})}
              placeholder="Enter target for this milestone"
              required
              data-testid="milestone-target-input"
            />
            <p className="text-xs text-slate-500">
              Goal total target: {formatCurrency(parentGoal.target_value, parentGoal.unit)}
            </p>
          </div>
        )}

        {milestone && (
          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger data-testid="milestone-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Open">Open</SelectItem>
                <SelectItem value="Closed">Closed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>Notes (optional)</Label>
          <Textarea
            value={form.notes}
            onChange={(e) => setForm({...form, notes: e.target.value})}
            placeholder="Any notes about this milestone..."
            data-testid="milestone-notes"
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={loading} data-testid="save-milestone-btn">
            {loading ? 'Saving...' : 'Save Milestone'}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
